// Audio context for breathing cues
let audioContext = null;

export const playTone = (frequency, duration, type = 'sine') => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  oscillator.frequency.value = frequency;
  oscillator.type = type;
  
  gainNode.gain.setValueAtTime(0, audioContext.currentTime);
  gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.1);
  gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + duration);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration);
};

// Metronome tick sound
export const playMetronomeTick = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  
  const now = audioContext.currentTime;
  
  // Create a short, sharp click sound
  const osc = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  osc.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  // Use a higher frequency for a crisp tick sound
  osc.frequency.value = 1000;
  osc.type = 'sine';
  
  // Very short envelope for click effect
  gainNode.gain.setValueAtTime(0.3, now);
  gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
  
  osc.start(now);
  osc.stop(now + 0.05);
};

// Voice announcement using Web Speech API
export const speakInstruction = (text, lang = 'fr-FR') => {
  if ('speechSynthesis' in window) {
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 0.75; // Slower for a calmer, more relaxed feel
    utterance.pitch = 0.9; // Slightly lower pitch for softer tone
    utterance.volume = 0.6; // Softer volume
    
    // Try to find a soft French voice (prefer female voices which tend to be softer)
    const voices = window.speechSynthesis.getVoices();
    
    // Priority: French female voice > French voice > any voice
    const frenchFemaleVoice = voices.find(voice => 
      voice.lang.startsWith('fr') && 
      (voice.name.toLowerCase().includes('female') || 
       voice.name.toLowerCase().includes('femme') ||
       voice.name.toLowerCase().includes('amélie') ||
       voice.name.toLowerCase().includes('aurelie') ||
       voice.name.toLowerCase().includes('marie') ||
       voice.name.toLowerCase().includes('thomas') === false)
    );
    
    const frenchVoice = voices.find(voice => voice.lang.startsWith('fr'));
    
    if (frenchFemaleVoice) {
      utterance.voice = frenchFemaleVoice;
    } else if (frenchVoice) {
      utterance.voice = frenchVoice;
    }
    
    window.speechSynthesis.speak(utterance);
  }
};

// Volume ducking system for breathing sounds during voice announcements
let breathingVolume = 0.7; // Default volume for breathing sounds
let volumeDuckingCallbacks = []; // Callbacks to notify when volume changes

export const getBreathingVolume = () => breathingVolume;

export const registerVolumeDuckingCallback = (callback) => {
  volumeDuckingCallbacks.push(callback);
  return () => {
    volumeDuckingCallbacks = volumeDuckingCallbacks.filter(cb => cb !== callback);
  };
};

const setBreathingVolume = (volume) => {
  breathingVolume = volume;
  volumeDuckingCallbacks.forEach(cb => cb(volume));
};

// Louder version for countdown announcements with volume ducking
export const speakCountdown = (text, lang = 'fr-FR') => {
  if ('speechSynthesis' in window) {
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    // Duck the breathing sounds volume
    setBreathingVolume(0.2);
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 0.9; // Slightly faster for clarity
    utterance.pitch = 1.1; // Slightly higher pitch to cut through breathing sounds
    utterance.volume = 1.0; // Full volume
    
    const voices = window.speechSynthesis.getVoices();
    const frenchVoice = voices.find(voice => voice.lang.startsWith('fr'));
    
    if (frenchVoice) {
      utterance.voice = frenchVoice;
    }
    
    // Restore volume when speech ends
    utterance.onend = () => {
      setBreathingVolume(0.7);
    };
    
    // Also restore on error
    utterance.onerror = () => {
      setBreathingVolume(0.7);
    };
    
    window.speechSynthesis.speak(utterance);
  }
};

// Initialize voices (needed for some browsers)
export const initVoices = () => {
  if ('speechSynthesis' in window) {
    // Load voices - this is async in some browsers
    window.speechSynthesis.getVoices();
    
    // Some browsers require this event listener
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
  }
};

// Tibetan singing bowl sound
export const playTibetanBowl = (pitch = 'high') => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  
  const now = audioContext.currentTime;
  const duration = 3; // Long resonance like a real bowl
  
  // Base frequency - higher for top, lower for bottom
  const baseFreq = pitch === 'high' ? 528 : 256; // 528Hz (high/healing) or 256Hz (low/grounding)
  
  // Create multiple oscillators for rich harmonic content (like a real singing bowl)
  const harmonics = [1, 2, 3, 4.5, 6]; // Singing bowls have unique harmonic series
  const gains = [0.4, 0.25, 0.15, 0.1, 0.05];
  
  // Master gain for overall volume
  const masterGain = audioContext.createGain();
  masterGain.connect(audioContext.destination);
  masterGain.gain.setValueAtTime(0.3, now);
  masterGain.gain.exponentialRampToValueAtTime(0.01, now + duration);
  
  harmonics.forEach((harmonic, i) => {
    const osc = audioContext.createOscillator();
    const oscGain = audioContext.createGain();
    
    osc.connect(oscGain);
    oscGain.connect(masterGain);
    
    osc.frequency.value = baseFreq * harmonic;
    osc.type = 'sine';
    
    // Each harmonic has its own envelope
    oscGain.gain.setValueAtTime(gains[i], now);
    oscGain.gain.exponentialRampToValueAtTime(0.001, now + duration * (1 - i * 0.1));
    
    // Slight frequency wobble for authenticity
    osc.frequency.setValueAtTime(baseFreq * harmonic, now);
    osc.frequency.linearRampToValueAtTime(baseFreq * harmonic * 0.998, now + duration);
    
    osc.start(now);
    osc.stop(now + duration);
  });
  
  // Add a subtle attack transient (the "strike" sound)
  const noise = audioContext.createBufferSource();
  const noiseBuffer = audioContext.createBuffer(1, audioContext.sampleRate * 0.05, audioContext.sampleRate);
  const noiseData = noiseBuffer.getChannelData(0);
  for (let i = 0; i < noiseData.length; i++) {
    noiseData[i] = (Math.random() * 2 - 1) * (1 - i / noiseData.length);
  }
  noise.buffer = noiseBuffer;
  
  const noiseFilter = audioContext.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.value = baseFreq * 2;
  noiseFilter.Q.value = 2;
  
  const noiseGain = audioContext.createGain();
  noiseGain.gain.setValueAtTime(0.1, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
  
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(audioContext.destination);
  
  noise.start(now);
};

// Short singing bowl chime for breathing cues
export const playBowlChime = (pitch = 'high') => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  
  const now = audioContext.currentTime;
  const duration = 1.2; // Shorter than full bowl but still resonant
  
  // Base frequency - higher for inhale, lower for exhale
  const baseFreq = pitch === 'high' ? 639 : 285; // Solfeggio frequencies
  
  // Harmonics for rich singing bowl sound
  const harmonics = [1, 2, 3, 4.5];
  const gains = [0.35, 0.2, 0.12, 0.06];
  
  // Master gain
  const masterGain = audioContext.createGain();
  masterGain.connect(audioContext.destination);
  masterGain.gain.setValueAtTime(0.25, now);
  masterGain.gain.exponentialRampToValueAtTime(0.01, now + duration);
  
  harmonics.forEach((harmonic, i) => {
    const osc = audioContext.createOscillator();
    const oscGain = audioContext.createGain();
    
    osc.connect(oscGain);
    oscGain.connect(masterGain);
    
    osc.frequency.value = baseFreq * harmonic;
    osc.type = 'sine';
    
    oscGain.gain.setValueAtTime(gains[i], now);
    oscGain.gain.exponentialRampToValueAtTime(0.001, now + duration * (1 - i * 0.15));
    
    // Subtle frequency wobble
    osc.frequency.setValueAtTime(baseFreq * harmonic, now);
    osc.frequency.linearRampToValueAtTime(baseFreq * harmonic * 0.997, now + duration);
    
    osc.start(now);
    osc.stop(now + duration);
  });
  
  // Soft attack transient
  const noise = audioContext.createBufferSource();
  const noiseBuffer = audioContext.createBuffer(1, audioContext.sampleRate * 0.03, audioContext.sampleRate);
  const noiseData = noiseBuffer.getChannelData(0);
  for (let i = 0; i < noiseData.length; i++) {
    noiseData[i] = (Math.random() * 2 - 1) * (1 - i / noiseData.length) * 0.5;
  }
  noise.buffer = noiseBuffer;
  
  const noiseFilter = audioContext.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.value = baseFreq * 2;
  noiseFilter.Q.value = 3;
  
  const noiseGain = audioContext.createGain();
  noiseGain.gain.setValueAtTime(0.08, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
  
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(audioContext.destination);
  
  noise.start(now);
};

// Realistic breathing sound using filtered noise
export const playBreathSound = (type, duration = 2) => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  
  const bufferSize = audioContext.sampleRate * duration;
  const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
  const data = buffer.getChannelData(0);
  
  // Generate pink noise (more natural sounding than white noise)
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < bufferSize; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.96900 * b2 + white * 0.1538520;
    b3 = 0.86650 * b3 + white * 0.3104856;
    b4 = 0.55000 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.0168980;
    data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
    b6 = white * 0.115926;
  }
  
  const source = audioContext.createBufferSource();
  source.buffer = buffer;
  
  // Create filters for breathing effect
  const lowpass = audioContext.createBiquadFilter();
  lowpass.type = 'lowpass';
  
  const highpass = audioContext.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.value = 100;
  
  const gainNode = audioContext.createGain();
  
  source.connect(highpass);
  highpass.connect(lowpass);
  lowpass.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  const now = audioContext.currentTime;
  
  if (type === 'inhale') {
    // Inhale: frequency rises, volume builds up
    lowpass.frequency.setValueAtTime(200, now);
    lowpass.frequency.linearRampToValueAtTime(800, now + duration);
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.15, now + duration * 0.3);
    gainNode.gain.linearRampToValueAtTime(0.2, now + duration * 0.7);
    gainNode.gain.linearRampToValueAtTime(0, now + duration);
  } else {
    // Exhale: frequency falls, volume fades out
    lowpass.frequency.setValueAtTime(600, now);
    lowpass.frequency.linearRampToValueAtTime(150, now + duration);
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.25, now + duration * 0.2);
    gainNode.gain.linearRampToValueAtTime(0.15, now + duration * 0.6);
    gainNode.gain.linearRampToValueAtTime(0, now + duration);
  }
  
  source.start(now);
  source.stop(now + duration);
  
  return source;
};

// Vigorous bellows exhale sound (short, powerful burst)
// Bellows-specific custom exhale sound
let bellowsCustomAudioPool = [];
let bellowsCustomAudioLoaded = false;
const BELLOWS_SOUND_DURATION = 800; // Stop after 800ms to allow full playback of short file

export const loadBellowsCustomAudio = () => {
  // Preload a pool of audio instances for rapid playback
  if (bellowsCustomAudioPool.length === 0) {
    for (let i = 0; i < 8; i++) {
      const audio = new Audio('/audio/bellows-exhale-custom.mp3');
      audio.preload = 'auto';
      if (i === 0) {
        audio.addEventListener('canplaythrough', () => {
          bellowsCustomAudioLoaded = true;
        }, { once: true });
      }
      bellowsCustomAudioPool.push(audio);
    }
  }
};

// Main bellows exhale function - uses custom audio file
export const playBellowsExhale = (volume = 0.6) => {
  if (bellowsCustomAudioPool.length === 0) {
    loadBellowsCustomAudio();
  }
  
  if (!bellowsCustomAudioLoaded) {
    // Fallback to synthesized sound while loading
    playBellowsExhaleSynthetic();
    return;
  }
  
  try {
    // Find an audio instance that's paused (force-stopped or ended)
    let audio = bellowsCustomAudioPool.find(a => a.paused);
    if (!audio) {
      // All are playing, use synthesized fallback instead of creating more
      playBellowsExhaleSynthetic();
      return;
    }
    
    audio.volume = Math.min(1, Math.max(0, volume));
    audio.currentTime = 0;
    audio.play().catch(e => {
      console.warn('Could not play bellows audio:', e);
      playBellowsExhaleSynthetic();
    });
    
    // Force-stop after BELLOWS_SOUND_DURATION to free up the instance for reuse
    setTimeout(() => {
      audio.pause();
      audio.currentTime = 0;
    }, BELLOWS_SOUND_DURATION);
  } catch (e) {
    console.warn('Could not play bellows custom audio:', e);
    playBellowsExhaleSynthetic();
  }
};

// Synthetic bellows exhale (fallback)
export const playBellowsExhaleSynthetic = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  
  const duration = 0.25; // Short, punchy exhale
  const bufferSize = audioContext.sampleRate * duration;
  const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
  const data = buffer.getChannelData(0);
  
  // Generate noise with quick attack and decay
  for (let i = 0; i < bufferSize; i++) {
    const t = i / bufferSize;
    // Quick attack, fast decay envelope
    const envelope = t < 0.1 
      ? t / 0.1 
      : Math.exp(-((t - 0.1) * 8));
    // Mix of noise frequencies for "whoosh" effect
    const noise = Math.random() * 2 - 1;
    data[i] = noise * envelope * 0.8;
  }
  
  const source = audioContext.createBufferSource();
  source.buffer = buffer;
  
  // Bandpass filter for breath-like quality
  const bandpass = audioContext.createBiquadFilter();
  bandpass.type = 'bandpass';
  bandpass.frequency.value = 800;
  bandpass.Q.value = 0.8;
  
  // Highpass to remove rumble
  const highpass = audioContext.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.value = 150;
  
  // Lowpass for smoothness
  const lowpass = audioContext.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = 3000;
  
  const gainNode = audioContext.createGain();
  gainNode.gain.value = 0.5;
  
  source.connect(highpass);
  highpass.connect(bandpass);
  bandpass.connect(lowpass);
  lowpass.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  const now = audioContext.currentTime;
  
  // Frequency sweep for "whoosh" effect
  bandpass.frequency.setValueAtTime(1200, now);
  bandpass.frequency.linearRampToValueAtTime(400, now + duration);
  
  source.start(now);
  source.stop(now + duration);
  
  return source;
};


// Realistic breathing sound from audio files
let inhaleAudio = null;
let exhaleAudio = null;
let inhaleAudioLoaded = false;
let exhaleAudioLoaded = false;
let inhaleNaturalDuration = 4; // Default, will be updated when loaded
let exhaleNaturalDuration = 1; // Default, will be updated when loaded

export const loadBreathingAudio = () => {
  if (!inhaleAudio) {
    inhaleAudio = new Audio('/audio/inhale-sound.mp3');
    inhaleAudio.addEventListener('canplaythrough', () => {
      inhaleAudioLoaded = true;
    });
    inhaleAudio.addEventListener('loadedmetadata', () => {
      inhaleNaturalDuration = inhaleAudio.duration;
    });
    inhaleAudio.load();
  }
  if (!exhaleAudio) {
    exhaleAudio = new Audio('/audio/exhale-sound.mp3');
    exhaleAudio.addEventListener('canplaythrough', () => {
      exhaleAudioLoaded = true;
    });
    exhaleAudio.addEventListener('loadedmetadata', () => {
      exhaleNaturalDuration = exhaleAudio.duration;
    });
    exhaleAudio.load();
  }
};

export const playRealisticBreath = (type = 'inhale', volume = 0.7, targetDuration = null) => {
  // Load audio if not already loaded
  if (!inhaleAudio || !exhaleAudio) {
    loadBreathingAudio();
  }
  
  try {
    if (type === 'inhale') {
      if (!inhaleAudio || !inhaleAudioLoaded) {
        // Fallback to synthesized sound if audio not ready
        playBreathSound('inhale', volume * 0.5);
        return;
      }
      const audioClone = inhaleAudio.cloneNode();
      audioClone.volume = Math.min(1, Math.max(0, volume));
      audioClone.currentTime = 0;
      
      // Adjust playback rate to match target duration
      if (targetDuration && targetDuration > 0) {
        const naturalDuration = inhaleNaturalDuration || inhaleAudio.duration || 4;
        // Clamp playback rate between 0.5 and 2.0 for reasonable sound quality
        const playbackRate = Math.min(2.0, Math.max(0.5, naturalDuration / targetDuration));
        audioClone.playbackRate = playbackRate;
      }
      
      audioClone.play();
    } else if (type === 'exhale') {
      if (!exhaleAudio || !exhaleAudioLoaded) {
        // Fallback to synthesized sound if audio not ready
        playBellowsExhale();
        return;
      }
      const audioClone = exhaleAudio.cloneNode();
      audioClone.volume = Math.min(1, Math.max(0, volume));
      audioClone.currentTime = 0;
      
      // Adjust playback rate to match target duration
      if (targetDuration && targetDuration > 0) {
        const naturalDuration = exhaleNaturalDuration || exhaleAudio.duration || 1;
        // Clamp playback rate between 0.5 and 2.0 for reasonable sound quality
        const playbackRate = Math.min(2.0, Math.max(0.5, naturalDuration / targetDuration));
        audioClone.playbackRate = playbackRate;
      }
      
      audioClone.play();
    }
  } catch (e) {
    console.warn('Could not play breathing audio:', e);
  }
};

// Preload the breathing audio on module load
if (typeof window !== 'undefined') {
  setTimeout(loadBreathingAudio, 1000);
}


// Bellows-specific exhale sound
let bellowsExhaleAudio = null;
let bellowsExhaleAudioLoaded = false;

export const loadBellowsExhaleAudio = () => {
  if (!bellowsExhaleAudio) {
    bellowsExhaleAudio = new Audio('/audio/bellows-exhale.m4a');
    bellowsExhaleAudio.addEventListener('canplaythrough', () => {
      bellowsExhaleAudioLoaded = true;
    });
    bellowsExhaleAudio.load();
  }
};

export const playBellowsExhaleSound = (volume = 0.7) => {
  if (!bellowsExhaleAudio) {
    loadBellowsExhaleAudio();
  }
  
  if (!bellowsExhaleAudio || !bellowsExhaleAudioLoaded) {
    // Fallback to synthesized sound
    playBellowsExhale();
    return;
  }
  
  try {
    const audioClone = bellowsExhaleAudio.cloneNode();
    audioClone.volume = Math.min(1, Math.max(0, volume));
    audioClone.currentTime = 0;
    audioClone.play();
  } catch (e) {
    console.warn('Could not play bellows exhale audio:', e);
    playBellowsExhale();
  }
};

// Preload bellows audio
if (typeof window !== 'undefined') {
  setTimeout(loadBellowsExhaleAudio, 1200);
  setTimeout(loadBellowsCustomAudio, 1100); // Preload custom audio
}


// Wim Hof specific inhale sound
let wimhofInhaleAudio = null;
let wimhofInhaleAudioLoaded = false;

export const loadWimhofInhaleAudio = () => {
  if (!wimhofInhaleAudio) {
    wimhofInhaleAudio = new Audio('/audio/wimhof-inhale.m4a');
    wimhofInhaleAudio.addEventListener('canplaythrough', () => {
      wimhofInhaleAudioLoaded = true;
    });
    wimhofInhaleAudio.load();
  }
};

export const playWimhofInhale = (volume = 0.7) => {
  if (!wimhofInhaleAudio) {
    loadWimhofInhaleAudio();
  }
  
  if (!wimhofInhaleAudio || !wimhofInhaleAudioLoaded) {
    // Fallback to generic breath sound
    playBreathSound('inhale', 1.5);
    return;
  }
  
  try {
    const audioClone = wimhofInhaleAudio.cloneNode();
    // Use dynamic breathing volume (for ducking during voice announcements)
    const effectiveVolume = Math.min(getBreathingVolume(), volume);
    audioClone.volume = Math.min(1, Math.max(0, effectiveVolume));
    audioClone.currentTime = 0;
    audioClone.play();
  } catch (e) {
    console.warn('Could not play Wim Hof inhale audio:', e);
    playBreathSound('inhale', 1.5);
  }
};

// Wim Hof specific exhale sound
let wimhofExhaleAudio = null;
let wimhofExhaleAudioLoaded = false;

export const loadWimhofExhaleAudio = () => {
  if (!wimhofExhaleAudio) {
    wimhofExhaleAudio = new Audio('/audio/wimhof-exhale-new.m4a');
    wimhofExhaleAudio.addEventListener('canplaythrough', () => {
      wimhofExhaleAudioLoaded = true;
    });
    wimhofExhaleAudio.load();
  }
};

export const playWimhofExhale = (volume = 0.7) => {
  if (!wimhofExhaleAudio) {
    loadWimhofExhaleAudio();
  }
  
  if (!wimhofExhaleAudio || !wimhofExhaleAudioLoaded) {
    // Fallback to synthesized sound
    playBellowsExhale();
    return;
  }
  
  try {
    const audioClone = wimhofExhaleAudio.cloneNode();
    // Use dynamic breathing volume (for ducking during voice announcements)
    const effectiveVolume = Math.min(getBreathingVolume(), volume);
    audioClone.volume = Math.min(1, Math.max(0, effectiveVolume));
    audioClone.currentTime = 0;
    audioClone.play();
  } catch (e) {
    console.warn('Could not play Wim Hof exhale audio:', e);
    playBellowsExhale();
  }
};

// Wim Hof SLOW mode - combined inhale+exhale audio
let wimhofSlowBreathAudio = null;
let wimhofSlowBreathAudioLoaded = false;

export const loadWimhofSlowBreathAudio = () => {
  if (!wimhofSlowBreathAudio) {
    wimhofSlowBreathAudio = new Audio('/audio/wimhof-slow-breath.mp3');
    wimhofSlowBreathAudio.addEventListener('canplaythrough', () => {
      wimhofSlowBreathAudioLoaded = true;
    });
    wimhofSlowBreathAudio.load();
  }
};

export const playWimhofSlowBreath = (volume = 0.7) => {
  if (!wimhofSlowBreathAudio) {
    loadWimhofSlowBreathAudio();
  }
  
  if (!wimhofSlowBreathAudio || !wimhofSlowBreathAudioLoaded) {
    // Fallback to regular breath sound
    playBreathSound('inhale', 2.5);
    return;
  }
  
  try {
    const audioClone = wimhofSlowBreathAudio.cloneNode();
    // Use dynamic breathing volume (for ducking during voice announcements)
    const effectiveVolume = Math.min(getBreathingVolume(), volume);
    audioClone.volume = Math.min(1, Math.max(0, effectiveVolume));
    audioClone.currentTime = 0;
    audioClone.play();
  } catch (e) {
    console.warn('Could not play Wim Hof slow breath audio:', e);
    playBreathSound('inhale', 2.5);
  }
};

// Alternate Nostril specific sounds
let alternateInhaleAudio = null;
let alternateInhaleAudioLoaded = false;
let alternateExhaleAudio = null;
let alternateExhaleAudioLoaded = false;

export const loadAlternateInhaleAudio = () => {
  if (!alternateInhaleAudio) {
    alternateInhaleAudio = new Audio('/audio/alternate-inhale.mp3');
    alternateInhaleAudio.addEventListener('canplaythrough', () => {
      alternateInhaleAudioLoaded = true;
    });
    alternateInhaleAudio.load();
  }
};

export const loadAlternateExhaleAudio = () => {
  if (!alternateExhaleAudio) {
    alternateExhaleAudio = new Audio('/audio/alternate-exhale.mp3');
    alternateExhaleAudio.addEventListener('canplaythrough', () => {
      alternateExhaleAudioLoaded = true;
    });
    alternateExhaleAudio.load();
  }
};

export const playAlternateInhale = (volume = 0.7) => {
  if (!alternateInhaleAudio) {
    loadAlternateInhaleAudio();
  }
  
  if (!alternateInhaleAudio || !alternateInhaleAudioLoaded) {
    playBreathSound('inhale', 4);
    return;
  }
  
  try {
    const audioClone = alternateInhaleAudio.cloneNode();
    audioClone.volume = Math.min(1, Math.max(0, volume));
    audioClone.currentTime = 0;
    audioClone.play();
  } catch (e) {
    console.warn('Could not play Alternate inhale audio:', e);
    playBreathSound('inhale', 4);
  }
};

export const playAlternateExhale = (volume = 0.7) => {
  if (!alternateExhaleAudio) {
    loadAlternateExhaleAudio();
  }
  
  if (!alternateExhaleAudio || !alternateExhaleAudioLoaded) {
    playBreathSound('exhale', 4);
    return;
  }
  
  try {
    const audioClone = alternateExhaleAudio.cloneNode();
    audioClone.volume = Math.min(1, Math.max(0, volume));
    audioClone.currentTime = 0;
    audioClone.play();
  } catch (e) {
    console.warn('Could not play Alternate exhale audio:', e);
    playBreathSound('exhale', 4);
  }
};

// Preload Wim Hof audio
if (typeof window !== 'undefined') {
  setTimeout(loadWimhofInhaleAudio, 1300);
  setTimeout(loadWimhofExhaleAudio, 1400);
  setTimeout(loadWimhofSlowBreathAudio, 1450);
  setTimeout(loadAlternateInhaleAudio, 1500);
  setTimeout(loadAlternateExhaleAudio, 1600);
}
